import { useState, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  Countries,
  CountryNameToISO,
  ISOToCountryName,
  RegistrationFieldLabels,
} from "../../../../config/RegistrationConfigs";
import useForm from "../../../../lib/common/AsyncFormBox/useForm";

/**
 * NationalityInput - Autocomplete component for selecting nationality
 * Displays country names in the UI but stores ISO codes as the value
 *
 * @param {Object} props
 * @param {string} [props.value] - Controlled ISO country code
 * @param {string} [props.defaultValue] - Default ISO country code (uncontrolled mode)
 * @param {Function} [props.onChange] - Callback when nationality changes, receives ISO code
 * @param {boolean} [props.disabled] - Whether the input is disabled
 */
export default function NationalityInput({ value, defaultValue = "", onChange, disabled = false }) {
  const form = useForm();
  const isControlled = value !== undefined;
  const [displayValue, setDisplayValue] = useState(null);

  /**
   * Converts an ISO code to a country name for display
   * @param {string} isoCode - ISO country code
   * @returns {string|null} Country name or null
   */
  function getCountryNameFromISO(isoCode) {
    if (!isoCode) return null;
    return ISOToCountryName[isoCode] || null;
  }

  /**
   * Converts a country name to ISO code
   * @param {string} countryName - Country name
   * @returns {string} ISO code
   */
  function getISOFromCountryName(countryName) {
    return CountryNameToISO[countryName] || "";
  }

  // Initialize display value from defaultValue or value (ISO code)
  useEffect(() => {
    const isoCode = isControlled ? value : defaultValue;
    if (isoCode) {
      const countryName = getCountryNameFromISO(isoCode);
      if (countryName) {
        setDisplayValue(countryName);
        form.setValue("nationality", isoCode);
      }
    } else {
      setDisplayValue(null);
      form.setValue("nationality", "");
    }
  }, [value, defaultValue, isControlled, form]);

  function handleChange(event, newValue) {
    const countryName = newValue || null;
    setDisplayValue(countryName);
    const isoCode = countryName ? getISOFromCountryName(countryName) : "";
    form.setValue("nationality", isoCode);
    onChange && onChange(isoCode);
  }

  return (
    <>
      {/* Hidden input to store the ISO code for form submission */}
      <input type="hidden" name="nationality" value={displayValue ? getISOFromCountryName(displayValue) : ""} />
      <Autocomplete
        options={Countries}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            required
            label={RegistrationFieldLabels.nationality}
            name="nationality"
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
              },
            }}
          />
        )}
      />
    </>
  );
}
